// src/pages/RegisterPage2.jsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser, fetchCurrentUser } from "../userSlice";
import { register } from "../services/authService";
import { acceptInvitation } from "../services/invitationService";
import { createJoinRequest } from "../services/joinRequestService";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Alert,
  useMediaQuery,
} from "@mui/material";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import { Google as GoogleIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import MeetingLogo from "../assets/Meet-ing-logo.jpg";

// Styled Components
const RegisterContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
}));

const TopBar = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "73px",
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(3, 2),
  zIndex: theme.zIndex.appBar,
  backdropFilter: 'blur(8px)',
  transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const MainContent = styled(Container)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4, 2),
  paddingTop: theme.spacing(14), // Add extra top padding for fixed header
  maxWidth: "600px",
}));

const RegisterPage2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Form validation state
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // URL parameters for invitations
  const [inviteToken, setInviteToken] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");

  // Extract URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("inviteToken");
    const e = params.get("inviteEmail");
    const orgIdParam = params.get("orgId");
    const orgNameParam = params.get("orgName");
    
    if (t) setInviteToken(t);
    if (e) setEmail(e);
    if (orgIdParam) setOrgId(orgIdParam);
    if (orgNameParam) setOrgName(decodeURIComponent(orgNameParam));
  }, [location.search]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Form validation
  const validateUsername = (username) => {
    if (!username) return "Username is required";
    if (username.length < 3) return "Username must be at least 3 characters";
    if (username.length > 30) return "Username must be less than 30 characters";
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    return "";
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    // Only validate if user has already attempted to submit
    if (hasAttemptedSubmit) {
      setUsernameError(validateUsername(value));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Only validate if user has already attempted to submit
    if (hasAttemptedSubmit) {
      setEmailError(validateEmail(value));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    // Only validate if user has already attempted to submit
    if (hasAttemptedSubmit) {
      setPasswordError(validatePassword(value));
    }
  };

  // Social login options data
  const socialLoginOptions = [
    {
      id: "google",
      icon: GoogleIcon,
      color: "#5F6368",
    },
  ];

  const handleSocialClick = (id) => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const byId = {
      google: `${base}/api/auth/oauth/google/start`,
      facebook: `${base}/api/auth/oauth/facebook/start`,
      apple: `${base}/api/auth/oauth/apple/start`,
    };
    const startUrl = byId[id];
    if (!startUrl) return;
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('inviteToken');
    const orgId = params.get('orgId');
    const orgName = params.get('orgName');
    const u = new URL(startUrl);
    if (inviteToken) u.searchParams.set('inviteToken', inviteToken);
    if (orgId) u.searchParams.set('orgId', orgId);
    if (orgName) u.searchParams.set('orgName', orgName);
    window.location.href = u.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark that user has attempted to submit (enables live validation)
    setHasAttemptedSubmit(true);
    
    // Validate form
    const usernameValidation = validateUsername(username);
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    setUsernameError(usernameValidation);
    setEmailError(emailValidation);
    setPasswordError(passwordValidation);
    
    if (usernameValidation || emailValidation || passwordValidation) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = await register(username, email, password, inviteToken);
      dispatch(setUser(data.user));
      dispatch(fetchCurrentUser());
      enqueueSnackbar(data.message || "Registered successfully", { variant: "success" });

      // if there's an inviteToken => accept and go to rooms
      if (inviteToken) {
        try {
          await acceptInvitation(inviteToken);
          enqueueSnackbar("Invitation accepted", { variant: "success" });
          navigate("/rooms");
        } catch (err) {
          enqueueSnackbar("Failed to accept invitation", { variant: "error" });
          navigate("/rooms"); // Still go to rooms even if invitation acceptance fails
        }
      } else if (orgId) {
        // Organization invitation link - create join request
        try {
          await createJoinRequest(orgId);
          enqueueSnackbar("Join request submitted successfully! Please wait for admin approval.", { variant: "success" });
          navigate("/join-pending", { state: { organizationName: orgName } });
        } catch (err) {
          console.error("Failed to create join request:", err);
          enqueueSnackbar("Registration successful, but failed to submit join request. Please try joining manually.", { variant: "warning" });
          navigate("/organization-choice");
        }
      } else {
        // No invite token and no org context - redirect to organization choice
        navigate("/organization-choice");
      }
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Registration failed", { variant: "error" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RegisterContainer>
      {/* Top Bar */}
      <TopBar>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Box 
            component={RouterLink}
            to="/"
            sx={{ display: "flex", alignItems: "center", textDecoration: 'none' }}
          >
            <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 128, height: 47 }} />
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button 
            component={RouterLink} 
            to="/login" 
            variant="outlined"
            sx={{
              borderRadius: "3px",
              textTransform: "none",
              fontFamily: theme.typography.fontFamily,
              fontWeight: 500,
              fontSize: "14px",
              px: 3,
              py: 1,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Login
          </Button>
        </Box>
      </TopBar>

      {/* Main Content */}
      <MainContent>
        {orgName && (
          <Alert severity="info" sx={{ mb: 3, width: "100%", maxWidth: "510px" }}>
            You're registering to join <strong>{orgName}</strong>. 
            After registration, your join request will be submitted for admin approval.
          </Alert>
        )}

        {/* Register Box */}
        <Box 
          sx={{ 
            width: "510px", 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            gap: 4,
            py: 4
          }}
        >
          {/* Welcome Section */}
          <Box sx={{ textAlign: 'center', mt: 0 }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                color: theme.palette.text.primary,
                fontSize: 50,
                lineHeight: 'normal'
              }}
            >
              Welcome to meetin!
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                color: theme.palette.text.secondary,
                fontSize: '1.875rem',
                textAlign: 'center',
                lineHeight: 'normal'
              }}
            >
              Let's build your rooms
            </Typography>
          </Box>
          
          {/* Register Section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "20px",
                textAlign: "center",
              }}
            >
              Register
            </Typography>
            
            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Username"
                placeholder="Enter your username"
                value={username}
                onChange={handleUsernameChange}
                required
                variant="outlined"
                disabled={isSubmitting}
                error={!!usernameError}
                helperText={usernameError}
                sx={{
                  width: "391px",
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    transition: theme.transitions.create([
                      'border-color',
                      'box-shadow',
                      'background-color',
                    ]),
                    // Default state
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                      borderWidth: "1px",
                    },
                    // Hover state
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                    },
                    // Error state
                    "&.Mui-error fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error:hover fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error.Mui-focused fieldset": {
                      borderColor: theme.palette.error.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.error.main}25`,
                    },
                    // Disabled state
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      "& fieldset": {
                        borderColor: theme.palette.action.disabled,
                      },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    padding: `${theme.spacing(0, 1.5)} !important`,
                    height: "60px",
                    lineHeight: "60px",
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "&:disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                  "& .MuiInputBase-root": {
                    padding: "0 !important",
                    alignItems: "center",
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                    "transform": "translate(12px, 20px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Email"
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                variant="outlined"
                disabled={isSubmitting}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  readOnly: !!inviteToken
                }}
                sx={{
                  width: "391px",
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    transition: theme.transitions.create([
                      'border-color',
                      'box-shadow',
                      'background-color',
                    ]),
                    // Default state
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                      borderWidth: "1px",
                    },
                    // Hover state
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                    },
                    // Error state
                    "&.Mui-error fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error:hover fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error.Mui-focused fieldset": {
                      borderColor: theme.palette.error.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.error.main}25`,
                    },
                    // Disabled state
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      "& fieldset": {
                        borderColor: theme.palette.action.disabled,
                      },
                    },
                    // Read-only state
                    "&.Mui-readOnly": {
                      backgroundColor: theme.palette.grey[50],
                      "& fieldset": {
                        borderColor: theme.palette.grey[300],
                      },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    padding: `${theme.spacing(0, 1.5)} !important`,
                    height: "60px",
                    lineHeight: "60px",
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "&:disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                  "& .MuiInputBase-root": {
                    padding: "0 !important",
                    alignItems: "center",
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                    "transform": "translate(12px, 20px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />
              
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                required
                variant="outlined"
                disabled={isSubmitting}
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isSubmitting}
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                          "&:disabled": {
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        <VisibilityOffOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "391px",
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    transition: theme.transitions.create([
                      'border-color',
                      'box-shadow',
                      'background-color',
                    ]),
                    // Default state
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                      borderWidth: "1px",
                    },
                    // Hover state
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                    },
                    // Error state
                    "&.Mui-error fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error:hover fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error.Mui-focused fieldset": {
                      borderColor: theme.palette.error.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.error.main}25`,
                    },
                    // Disabled state
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      "& fieldset": {
                        borderColor: theme.palette.action.disabled,
                      },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    padding: `${theme.spacing(0, 1.5)} !important`,
                    paddingRight: "0 !important", // Remove right padding for endAdornment
                    height: "60px",
                    lineHeight: "60px",
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "&:disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                  "& .MuiInputBase-root": {
                    padding: "0 !important",
                    paddingRight: `${theme.spacing(1.5)} !important`, // Add right padding for endAdornment
                    alignItems: "center",
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                    "transform": "translate(12px, 20px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />
              
              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={isSubmitting || !!usernameError || !!emailError || !!passwordError}
                sx={{
                  width: "391px",
                  height: "60px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.customShadows.primaryButton,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  transition: theme.transitions.create([
                    'background-color',
                    'box-shadow',
                    'opacity',
                  ]),
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: theme.customShadows.primaryButtonHover,
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    boxShadow: "none",
                  },
                }}
              >
                {isSubmitting ? "Registering..." : "Register"}
              </Button>
            </Box>
          </Box>
          
          {/* Social Login Section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              mt: 4,
            }}
          >
            {/* Register with divider */}
            <Box 
              sx={{ 
                width: "287px", 
                display: "flex", 
                alignItems: "center", 
                gap: 2 
              }}
            >
              <Divider sx={{ flexGrow: 1, backgroundColor: theme.palette.text.disabled }} />
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 400,
                  color: theme.palette.text.disabled,
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                Sign up with
              </Typography>
              <Divider sx={{ flexGrow: 1, backgroundColor: theme.palette.text.disabled }} />
            </Box>
            
            {/* Social Icons */}
            <Box
              sx={{
                width: "287px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={() => handleSocialClick('google')}
                sx={{
                  width: "67px",
                  height: "67px",
                  borderRadius: "33.5px",
                  border: `1px solid ${theme.palette.text.disabled}`,
                }}
              >
                <GoogleIcon 
                  sx={{ 
                    color: "#5F6368",
                    fontSize: "30px"
                  }} 
                />
              </IconButton>
            </Box>
          </Box>
          
          {/* Sign In Link */}
          <Link
            component={RouterLink}
            to="/login"
            underline="none"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 400,
              color: theme.palette.primary.main,
              fontSize: "16px",
              textAlign: "center",
              mt: 4,
            }}
          >
            Already have an account? Sign in here
          </Link>
        </Box>
      </MainContent>
    </RegisterContainer>
  );
};

export default RegisterPage2;
